import { WebchatMessage } from '@/services/webchat-service/webchat.service.types';
import { useWebchatStore } from '@/store/webchat.store';
import dayjs from 'dayjs';
import clsx from 'clsx';
import styles from './chat-bubble.module.scss';

interface ChatBubbleProps {
  message: WebchatMessage;
}

// Picks which of the two stored texts to render. Visitor messages store
// `original_text` in the visitor's language and `translated_text` in English;
// agent/bot messages are the inverse. Fall back to `original_text` whenever
// the opposite side is null so a translation failure never leaves a blank bubble.
const pickText = (message: WebchatMessage, showOriginal: boolean): string => {
  const wantsVisitorLanguage = showOriginal;
  if (message.sender_type === 'visitor') {
    return wantsVisitorLanguage
      ? message.original_text
      : (message.translated_text ?? message.original_text);
  }
  return wantsVisitorLanguage
    ? (message.translated_text ?? message.original_text)
    : message.original_text;
};

export const ChatBubble = ({ message }: ChatBubbleProps) => {
  const showOriginal = useWebchatStore((s) => s.showOriginalTranscript);
  const text = pickText(message, showOriginal);
  const time = dayjs(message.created_at).format('HH:mm');

  if (message.sender_type === 'system') {
    return (
      <div className={styles.systemMessage}>
        <span className={styles.systemText}>{text}</span>
      </div>
    );
  }

  const side = message.sender_type === 'visitor' ? 'visitor' : message.sender_type;

  return (
    <div className={clsx(styles.bubbleWrapper, styles[side])}>
      {message.sender_type !== 'visitor' && message.sender_name && (
        <span className={styles.senderLabel}>{message.sender_name}</span>
      )}
      <div className={clsx(styles.bubble, styles[side])}>{text}</div>
      <span className={styles.timestamp}>{time}</span>
    </div>
  );
};
